/* eslint-disable guard-for-in */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable max-len */
import { ChangeDetectorRef, Component, effect, EventEmitter, input, Input, OnDestroy, OnInit, output, Output, Signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import { LocalStorageService } from '../../core/services/localstorage.service';
import { debounceTime, distinctUntilChanged, share, switchMap, take, takeLast } from 'rxjs/operators';
import { DataCollectorService } from '../../core/services/data_collector_services/data_collector_service.service';
import { EditorOptions, SuggestedFields } from './text-editor.interface';
import { TextEditorService } from './text-editor.service';
import { BehaviorSubject, of, Subscription} from 'rxjs';
import { UtilityService } from '../../core/services/utility_functions/utility_functions.service';
import { TranslateService } from '@ngx-translate/core';
import { EditorComponent } from '@tinymce/tinymce-angular';

@Component({
  selector: 'text-editor',
  templateUrl: 'text-editor.component.html',
  styleUrls: ['./text-editor.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TextEditorComponent implements OnInit, OnDestroy {
  @Input() editorData: UntypedFormControl = null;
  @Input() options: EditorOptions = { module: '' };
  @Input() isEdit: boolean = false;
  @Input() initEditor: boolean;
  templateUid?: Signal<string> = input<string>();
  templateUidChange = output<string>();
  @Output() templateChange = new EventEmitter<any>();
  @Output() editorFocus = new EventEmitter<boolean>();
  @Output() attachmentsAdded = new EventEmitter<any>();
  @ViewChild('editor', { static: false }) editor: EditorComponent;
  @ViewChild('ngSelect') ngSelect: any;
  defaultOptions =  {
    attachmentType: '',
    allowMentions: true,
    imageUpload: false,
    showTemplate: true,
    isForm: false,
    placeholder: 'Type here...',
    module: '',
    addAttachmentsInBody: true
  };
  templateID: any = null;
  mockTemplateID: any = null;
  tinyMceInitiated: boolean = false;
  suggestedFields: Array<SuggestedFields> =  [];

  dropDownValues: any = [];
  currentText = '';
  mockText = '';
  searchSub$ = new BehaviorSubject<string>('');
  search$ = this.searchSub$.asObservable().pipe(
    distinctUntilChanged((prev, next) => prev === next),
    debounceTime(500),
    take(1),
    takeLast(1),
    share(),
    switchMap((query: any): any =>this.setMentionsValue(query))
  );
  templateModuleNames: any = {
    'SERVICE_CONTRACT': 'SERVICE_CONTRACTS',
    'ASSET': 'ASSETS'
  };

  config = {
    init: {
      id: this.options.editorId ?? 'text-editor',
      menubar: false,
      auto_focus: 'text-editor',
      toolbar_location: 'bottom',
      statusbar: false,
      base_url: '/tinymce',
      branding: false,
      height: '100%',
      placeholder: '',
      plugins: ['anchor', 'autolink', 'codesample', 'fullscreen', 'help',
      'image', 'lists', 'media', 'preview',
      'searchreplace', 'table', 'visualblocks'],
      imagetools_toolbar: 'rotateleft rotateright | flipv fliph | editimage imageoptions',
      block_formats: 'Paragraph=p; Header 1=h1; Header 2=h2; Header 3=h3; Header 4=h4; Header 5=h5; Header 6=h6',
      toolbar:
        'insertfile undo redo | formatselect | bold italic backcolor \
          bullist numlist | removeformat table | ',
      table_style_by_css: true,
      content_css : false,
      content_style: `
        .mce-content-body {
          font-size: 14px;
        }
        
        table {
          border: 1px solid #ccc;
          border-collapse: collapse;
        }
        
        td {
          border: 1px solid #ccc;
        }
        
        table[style*="border-spacing"]:not([style*="border-spacing: 0"]) {
          border-collapse: separate;
        }
        
        .mce-content-body {
          font-size:14px
        }
        `,
      elementpath: false,
      relative_urls : false,
      remove_script_host : false,
      autolink: false,
      convert_urls: false,
      setup: (editor: any): void => {
          editor.on('drop', (evnt: any): void=>{
            evnt.preventDefault();
            const fileList: FileList | null = evnt.dataTransfer.files;
            this.fileUpload(fileList);
          });

          editor.on('paste', (evnt: any): void=>{
            const fileList: FileList | null = (evnt.clipboardData || evnt.originalEvent.clipboardData).files;
            if(Array.from(fileList)?.length){
              evnt.preventDefault();
              this.fileUpload(fileList);
            }
          });
          /* Basic button that just inserts the date */
          editor.ui.registry.addButton('imageButton', {
            icon:'image',
            tooltip: 'Select Image',
            onAction: (_) => {
              const input = document.createElement('input');
              input.setAttribute('type', 'file');
              input.setAttribute('multiple', 'true');
              input.setAttribute('accept', 'image/*');
              input.click();
              input.addEventListener('change', (event: Event) => {
                const element = event.currentTarget as HTMLInputElement;
                const fileList: FileList | null = element.files;
                Array.from(fileList).forEach((file: any) => {
                  const formData = new FormData();
                  formData.append('attachment',file);
                  formData.append('attachment_type',this.options.attachmentType || '');
                  formData.append('content_type', file.type);
                  this.toast.loading('Image upload is in progress',{id:'imageUpload'});
                  this.datacollectorservice.uploadfile(this.textEditorService.textEditorApiUrls.upload,formData).subscribe((response: any)=>{
                    // eslint-disable-next-line quotes
                    const appendContent = `<br> <img src="${response.data.attachment_path}" alt="image">`;
                    const patchValue = `${this.editorData.value}${appendContent}`;
                    this.toast.close('imageUpload');
                    this.editorData.patchValue(patchValue);
                  });
                });
              });
            }
          });

          /* example, adding a toolbar menu button */
          if(this.suggestedFields && this.suggestedFields?.length > 0){
          editor.ui.registry.addMenuButton('dropdown', {
            icon:'code-sample',
            tooltip: 'Suggested Fields',
            search: {
              placeholder: 'Search Components...'
            },
            classes:'text-xl',
            fetch: (callback, fetchContext) => {
              if (fetchContext.pattern.length > 0) {
                let results = this.textEditorService.getSuggestedFields(this.getMatchedChars(fetchContext.pattern)).filter((data: any)=>data?.value !== 'field_header');
                const defaultValue = {
                  'type': 'cardmenuitem',
                  'value': 'field_placeholder',
                  'text': this.translateService.instant('text.no_results_found'),
                  'items': [
                      {
                          'type': 'cardcontainer',
                          'direction': 'vertical',
                          'items': [
                              {
                                  'type': 'cardtext',
                                  'text': this.translateService.instant('text.no_results_found'),
                                  'classes': [
                                      'text-base',
                                      'primary-font',
                                      'p-1'
                                  ]
                              }
                          ]
                      }
                  ]
                };
                results = results?.length === 0 ? [defaultValue] : results;
                const items = results?.map((data: any)=>({
                  type: 'togglemenuitem',
                  text: data.text,
                  value: data.value,
                  classes: '',
                  onAction: (): void => {
                    if (!['field_header', 'field_placeholder'].includes(data?.value)) {
                      editor.insertContent(data?.value);
                    }
                  }
                }));
                callback(items);
              }
              else{
                const items = this.suggestedFields.map((data: any)=>({
                  type: 'togglemenuitem',
                  text: data.text,
                  value: data.value,
                  classes: '',
                  onAction: (): void => {
                    if (data?.value !== 'field_header') {
                      editor.insertContent(data?.value);
                    }
                  }
                }));
                callback(items);
              }
            }
          });
        }

        editor.ui.registry.addAutocompleter('suggestedFields', {
          ch: '{',
          minChars: 0,
          rows: 'auto',
          columns: 1,
          onAction: (autocompleteApi, range, value) => {
            if (value !== 'field_header') {
              editor.selection.setRng(range);
              editor.insertContent(value);
              autocompleteApi.hide();
            }
          },
          fetch: (pattern: any) => new Promise((resolve: any) => {
              const results = this.textEditorService.getSuggestedFields(this.getMatchedChars(pattern));
              resolve(results);
            })
        });
        editor.ui.registry.addAutocompleter('suggestedUsers', {
          ch: '@',
          minChars: 0,
          rows: 'auto',
          onAction: (autocompleteApi, range, value) => {
            editor.selection.setRng(range);
            const recordData = JSON.parse(value);
            const windowOrigin = window.location.origin;
            const routePath = `${windowOrigin}/settings?page=EMPLOYEE_DETAILS&uid=${recordData.user_uid}`;
            // eslint-disable-next-line quotes
            const content = `<a href="${routePath}" class="underline text-primary" name="${recordData.user_uid}">@${recordData.first_name} ${recordData.last_name}</a> `;
            editor.insertContent(content);
            autocompleteApi.hide();
          },
          fetch: (pattern: any) => {
            let result: any = [];
            if (this.options.allowMentions) {
              this.searchSub$.next(pattern);
              result = this.search$
                .toPromise()
                .then((response: any) => {
                  if(response?.data){
                    return this.constructMentionsData(response.data);
                  }
                })
                .catch(console.error);
            }
            return result;
          }
        });
    }
  }
  };
  focusEditorSubscription: Subscription;
  selectedField: any = null;

  constructor(private textEditorService: TextEditorService,
              private datacollectorservice: DataCollectorService,
              private toast: HotToastService,
              private localStorageService: LocalStorageService,
              private utilityService: UtilityService,
              private translateService: TranslateService,
              private changeDetectorRef: ChangeDetectorRef
            ){
              effect(()=>{
                if(this.templateUid()){
                  this.templateID = this.templateUid();
                }
              });
            }

  getMatchedChars = (pattern: string): any =>  {
    const suggestedFields = this.options.suggestedFields && this.options.suggestedFields.length ? this.options.suggestedFields : this.suggestedFields;
    return suggestedFields.filter((char: any)=>char?.label?.toLowerCase().includes(pattern?.toLowerCase()) );
  };

  setMentionsValue(term): any{
    const paramObj = {
      'limit':20,
      'page':1,
      'filter_rules':[
        {
          'key':'keyword',
          'field_type':'TEXT',
          'value': term,
          'operator':'CONTAINS',
          'type':'default_field'
        }
      ],
      'sort_by':'created_at',
      'sort':'ASC'
    };
    const dataUrl = `${this.datacollectorservice.baseurl()}/users/filter`;
    this.currentText = term;
      if(this.currentText !== this.mockText){
        this.mockText = term;
        return this.datacollectorservice.postData(dataUrl, paramObj);
      }else{
        return of({data:[]});
      }
  }

  showEditor(): void {
    this.config.init.auto_focus= this.options?.id ? this.options?.id : 'text-editor';
    this.config.init.toolbar = this.options.showTable !== false ?
    'insertfile undo redo | formatselect | bold italic backcolor \
     bullist numlist | removeformat blocks table ' :
    'insertfile undo redo | formatselect | bold italic backcolor \
     bullist numlist | removeformat blocks ';
    if (this.options.showTemplate && !this.options.isForm) {
      this.config.init.toolbar = this.config.init.toolbar + 'dropdown';
    }
    this.tinyMceInitiated = true;
    this.changeDetectorRef.detectChanges();
    
    this.editorFocus.emit(true);
  }

  constructMentionsData(recordData): any{
    return recordData.map((data: any)=>({
                  type: 'autocompleteitem',
                  value: JSON.stringify(data),
                  text: `${data.first_name} ${data.last_name}`
                  }));
  }

  ngOnInit(): void{
    this.tinyMceInitiated = false;
    this.options = {...this.options};
    this.suggestedFields = this.options.suggestedFields && this.options.suggestedFields.length ? this.options.suggestedFields : this.suggestedFields;
    this.suggestedFields = this.textEditorService.getSuggestedFields(this.getMatchedChars('')).filter((data: any)=>data?.value !== 'field_header');

    this.config.init.placeholder = this.options.placeholder;
    if(this.options?.id){
      this.config.init.id = this.options?.id;
      this.config.init.auto_focus = this.options?.id;
    }
    if(this.options.imageUpload){
      this.config.init.toolbar = this.config.init.toolbar + 'imageButton';
    }
    if (this.options.showTemplate) {
      this.getModuleBasedTemplate();
    } else if(!this.options.initUponFocus){
      setTimeout(() => {
        this.showEditor();
      });
    }
    this.focusEditorSubscription = this.textEditorService.focusEditor.subscribe((data: any) => {
      let canShow: boolean = data === true ? true : false;
      if (data?.editorId === this.options?.editorId && data?.canInitialize) {
        canShow = true;
      }
      if (canShow) {
        this.showEditor();
      } else {
        this.tinyMceInitiated = false;
      }
    });
  }

  onTemplateClick(event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.ngSelect) {
      this.ngSelect.open();
    }
  }

  handleDropdownOpen(): void {
    // Force the dropdown to recalculate its position
    setTimeout(() => {
      if (this.ngSelect) {
        this.ngSelect.dropdownPanel.updateDropdownPosition();
      }
    });
  }

  getModuleBasedTemplate(): void{
    const params = {
      'count': 50,
      'filter.template_module': this.options.module,
      'page': 1,
      'sort': 'ASC',
      'sort_by': 'created_at'
    };
    this.datacollectorservice.getData(this.textEditorService?.textEditorApiUrls?.templateUrl, params).subscribe((response: any)=>{
      const companyConfig = this.localStorageService.getLocalStorage('company_configuration_data');
      this.dropDownValues = response?.data;
      if(this.templateUid()){
        this.templateID = this.templateUid();
      }else if(this.options.module === 'INVOICE' || this.options.module === 'INVOICES'){
        this.templateID = companyConfig?.invoice?.default_email_template ?? null;
      }
      else if(this.options.module === 'ESTIMATE' || this.options.module === 'ESTIMATES'){
        this.templateID = companyConfig?.estimate?.default_email_template ?? null;
      }
      else if(this.options.module === 'JOB'){
        this.templateID = companyConfig?.jobs?.default_email_template ?? null;
      }
      else if(this.options.module === 'SERVICE_CONTRACT'){
        if(!this.isEdit){
        this.templateID = companyConfig?.service_contract?.default_email_template ?? null;
        }
      }
      if(this.templateID){
        this.mockTemplateID = this.templateID;
        this.datacollectorservice.getData(`${this.textEditorService?.textEditorApiUrls?.templateUrl}/${this.templateID}`).subscribe((responseData: any)=>{
          this.getParsedTemplateBody(responseData);
        });
      }
      this.showEditor();
    });
  }

  getParsedTemplateBody(responseData): any{
    if(this.options?.moduleId){
      const payload = {
        module: this.options.module === 'SERVICE_CONTRACT' || this.options.module === 'ASSET' ? this.templateModuleNames[this.options.module] : this.options.module,
        module_uid: this.options?.moduleId,
        template_body: responseData?.data?.template_body ?? '',
        template_subject: responseData?.data?.template_subject ?? ''
      };
      this.datacollectorservice.postData(this.textEditorService?.textEditorApiUrls?.previewTemplateUrl,payload).subscribe({
        next: (response: any) => {
          this.populateTemplate(response);
          const previewData: any = {
            ...responseData?.data,
            ...response?.data
          };

          this.templateChange.emit(previewData);
        },
        error: (err: any) => {
          this.editorData.setValue('');
          this.templateChange.emit('');
        }
      });
    }else{
      this.editorData.setValue(responseData?.data?.template_body);
      this.templateChange.emit(responseData?.data);
    }
  }

  populateTemplate(responseData): any{
    this.editorData.setValue(responseData?.data?.body);
  }

  getAttachmentType(attachment: string): string {
    let type: string = 'file';
    if (attachment) {
        const splittedValue: string[] = attachment.split('.');
        type = splittedValue[splittedValue.length - 1];
    }
    return type;
  }

  getAttachmentfileName(attachment: any): string {
    let type: string = 'file';
    if (attachment) {
        const splittedValue: string[] = attachment.split('/');
        type = splittedValue[splittedValue.length - 1];
    }
    return type;
  }

  fileUpload(fileList): void{
    const attachmentArray = [];
    if(Array.from(fileList)?.length){
      const loader = this.toast.loading(this.utilityService.getLocaleValue('edit.note.attachment.uploading'));
      Array.from(fileList).forEach((file: any,index) => {
        const formData = new FormData();
        formData.append('attachment',file);
        formData.append('module', this.options?.module ?? '');
        formData.append('module_uid', this.options?.moduleId ?? '');
        formData.append('attachment_type', this.options?.attachmentType ?? 'JOB_NOTE');
        formData.append('attachment_name', file.name);
        this.datacollectorservice.uploadfile(this.textEditorService.textEditorApiUrls.upload,formData).subscribe((response: any)=>{
          // eslint-disable-next-line quotes
          if(this.options?.addAttachmentsInBody !== false){
            const appendContent = `<br> <img src="${response.data.attachment_path}" alt="image">`;
            const patchValue = `${this.editorData.value}${appendContent}`;
            this.editorData.patchValue(patchValue);
          }
          const attachmentType: string = this.getAttachmentType(response.data.attachment_path);
          const attachmentData: any = {
              attachment: response.data.attachment_path,
              attachment_uid: response.data.attachment_uid,
              attachment_name: response.data.attachment_name ?? this.getAttachmentfileName(response.data.attachment_path),
              attachment_size: response.data.attachment_size,
              attachment_type: this.utilityService.getAttachmentTypeEnum(attachmentType)
          };
          attachmentArray.push(attachmentData);
          if((fileList?.length - 1) === index){
            this.attachmentsAdded.emit(attachmentArray);
            loader.close();
          }
        });
      });
    }
  }

  get getConfig(): any{
    return this.config.init;
  }

  onTemplateChange(event): any{
    if(event?.template_uid){
      this.templateID =  event?.template_uid;
      this.templateUidChange.emit(event?.template_uid);
      if(this.templateID !== this.mockTemplateID){
        this.datacollectorservice.getData(`${this.textEditorService?.textEditorApiUrls?.templateUrl}/${event?.template_uid}`).subscribe((response: any)=>{
          this.mockTemplateID = event?.template_uid;
          this.getParsedTemplateBody(response);
        });
      }
    }else{
      this.templateID = null;
      this.mockTemplateID = null;
      this.editorData.setValue('');
      this.templateChange.emit('');
      this.templateUidChange.emit('');
    }
  }

  ngOnDestroy(): void{
    this.tinyMceInitiated = false;
    this.focusEditorSubscription.unsubscribe();
    if (this.editor) {
      this.editor?.editor?.destroy();
    }
  }
}


